import { useState } from "react";

import { ConfettiColored, EmptystateTimelineColored } from "components/icons/generated";
import Box from "ds/components/Box";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import EmptyState from "ds/components/EmptyState";
import Tab from "ds/components/Tab";
import TabPanel from "ds/components/TabPanel";
import { useToggle } from "hooks/useToggle";

import {
  RUN_OBSERVABILITY_FINDING_SEVERITIES,
  RUN_OBSERVABILITY_FINDING_SEVERITIES_BY_RANK,
} from "./constants";
import RunObservabilityFindingsRow from "./Row";
import styles from "./styles.module.css";
import { RunObservabilityFinding, RunObservabilityFindingSeverity } from "./types";
import {
  getDefaultRunObservabilityFindingSeverity,
  groupRunObservabilityFindingsBySeverity,
} from "./utils";

const NO_FINDINGS_TITLE = "No findings for this run.";

type RunObservabilityFindingsProps = {
  findings: RunObservabilityFinding[];
  attentionCaption?: string;
};

const RunObservabilityFindings = ({
  findings,
  attentionCaption,
}: RunObservabilityFindingsProps) => {
  const findingsBySeverity = groupRunObservabilityFindingsBySeverity(findings);
  const [activeSeverity, setActiveSeverity] = useState(() =>
    getDefaultRunObservabilityFindingSeverity(findingsBySeverity)
  );
  const [isCollapsed, toggle] = useToggle(false);

  const hasFindings = findings.length > 0;

  return (
    <CardCollapsible
      title="What we found"
      ariaLevel={3}
      ariaLabel="What we found"
      isCollapsed={isCollapsed}
      onToggle={toggle}
      contentGap="x-large"
    >
      {!hasFindings && (
        <EmptyState
          icon={attentionCaption ? EmptystateTimelineColored : ConfettiColored}
          title={NO_FINDINGS_TITLE}
          caption={attentionCaption}
        />
      )}

      {hasFindings && (
        <Box
          direction="row"
          align="center"
          gap="medium"
          padding="small 0 0 0"
          role="tablist"
          aria-label="Findings by severity"
        >
          {RUN_OBSERVABILITY_FINDING_SEVERITIES_BY_RANK.map((severity) => (
            <Tab<RunObservabilityFindingSeverity>
              key={severity}
              id={severity}
              size="small"
              isActive={severity === activeSeverity}
              onClick={setActiveSeverity}
              label={RUN_OBSERVABILITY_FINDING_SEVERITIES[severity].label}
              suffixComponent={
                <Counter
                  size="small"
                  count={findingsBySeverity[severity].length}
                  variant={RUN_OBSERVABILITY_FINDING_SEVERITIES[severity].counter}
                />
              }
            />
          ))}
        </Box>
      )}

      {hasFindings &&
        RUN_OBSERVABILITY_FINDING_SEVERITIES_BY_RANK.map((severity) => (
          <TabPanel
            key={severity}
            id={severity}
            isActive={severity === activeSeverity}
            gap="x-large"
            className={styles.list}
            scrollable
            tabIndex={0}
          >
            {findingsBySeverity[severity].length === 0 && (
              <EmptyState
                icon={ConfettiColored}
                title={RUN_OBSERVABILITY_FINDING_SEVERITIES[severity].emptyTitle}
              />
            )}

            {findingsBySeverity[severity].map((finding, index) => (
              <RunObservabilityFindingsRow key={index} {...finding} />
            ))}
          </TabPanel>
        ))}
    </CardCollapsible>
  );
};

export default RunObservabilityFindings;
